/**
 * DeviationTrendChart — Time-series line/area chart of mean / max / within-tol%
 * pulled from /scan3d/deviation-comparisons/_stats/trend.
 *
 * Used inside the Scan-to-Model "Trends" tab. Lets users spot drift, quality
 * regressions across rebuild iterations, and improvement over time.
 */
import { useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend, ReferenceLine } from 'recharts';
import { TrendingUp, Filter, AlertCircle, CheckCircle2 } from 'lucide-react';

export default function DeviationTrendChart({ projectId }) {
  const [models, setModels] = useState([]);
  const [modelId, setModelId] = useState('');
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!projectId) return;
    api.get(`/v1/projects/${projectId}/scan3d/models?isLatest=true`)
      .then(r => setModels(r.data?.models || []))
      .catch(() => setModels([]));
  }, [projectId]);

  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    api.get(`/v1/projects/${projectId}/scan3d/deviation-comparisons/_stats/trend`, {
      params: { ...(modelId ? { modelId } : {}), limit: 60 },
    })
      .then(r => setSeries(r.data?.series || []))
      .catch(() => setSeries([]))
      .finally(() => setLoading(false));
  }, [projectId, modelId]);

  const chartData = useMemo(() => series.map((s) => ({
    ...s,
    date: s.executedAt ? new Date(s.executedAt).toLocaleDateString('en-CA') : '—',
    label: s.comparisonCode,
  })), [series]);

  const stats = useMemo(() => {
    if (series.length === 0) return null;
    const first = series[0];
    const last = series[series.length - 1];
    return {
      runs: series.length,
      meanDelta: last.meanDeviationMm - first.meanDeviationMm,
      withinTolDelta: last.withinTolerancePct - first.withinTolerancePct,
      criticalSum: series.reduce((a, s) => a + (s.criticalZoneCount || 0), 0),
    };
  }, [series]);

  return (
    <div className="space-y-3" data-testid="trend-chart-tab">
      <div className="rounded-xl p-3 flex flex-wrap items-end gap-2" style={{ background: '#0f1722', border: '1px solid #1f2937' }}>
        <div className="flex items-center gap-1 text-xs opacity-70 mr-2">
          <TrendingUp className="w-3 h-3" /> Deviation Trend
        </div>
        <label className="text-xs">
          <span className="opacity-70 flex items-center gap-1"><Filter className="w-3 h-3" /> Filter by model</span>
          <select value={modelId} onChange={e => setModelId(e.target.value)}
                  className="block mt-1 px-2 py-1 rounded min-w-[200px]"
                  style={{ background: '#0a0e14', border: '1px solid #1f2937', color: '#fff' }}
                  data-testid="trend-model-select">
            <option value="">— All models —</option>
            {models.map(m => <option key={m.id} value={m.id}>{m.modelCode} · {m.modelName}</option>)}
          </select>
        </label>
        {stats && (
          <div className="ml-auto grid grid-cols-4 gap-3 text-[11px]">
            <div className="text-center">
              <div className="opacity-60">Runs</div>
              <div className="font-bold text-base">{stats.runs}</div>
            </div>
            <div className="text-center">
              <div className="opacity-60">Δ Mean dev</div>
              <div className={`font-bold text-base ${stats.meanDelta > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                {stats.meanDelta > 0 ? '+' : ''}{stats.meanDelta.toFixed(2)}mm
              </div>
            </div>
            <div className="text-center">
              <div className="opacity-60">Δ In-tol %</div>
              <div className={`font-bold text-base ${stats.withinTolDelta < 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                {stats.withinTolDelta > 0 ? '+' : ''}{stats.withinTolDelta.toFixed(1)}%
              </div>
            </div>
            <div className="text-center">
              <div className="opacity-60">Critical zones (Σ)</div>
              <div className="font-bold text-base text-red-400">{stats.criticalSum}</div>
            </div>
          </div>
        )}
      </div>

      <div className="rounded-xl p-3" style={{ background: '#0f1722', border: '1px solid #1f2937' }} data-testid="trend-chart-mean-max">
        <div className="text-[10px] uppercase tracking-wider opacity-60 mb-2">Mean / Max deviation (mm)</div>
        {loading ? (
          <div className="text-xs opacity-50 py-10 text-center">Loading…</div>
        ) : chartData.length === 0 ? (
          <div className="text-xs opacity-50 py-10 text-center">No executed comparisons yet for this filter.</div>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={chartData}>
              <CartesianGrid stroke="#1f2937" strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fill: '#9ca3af', fontSize: 10 }} />
              <YAxis tick={{ fill: '#9ca3af', fontSize: 10 }} unit="mm" />
              <Tooltip
                contentStyle={{ background: '#0a0e14', border: '1px solid #1f2937', fontSize: 11 }}
                labelStyle={{ color: '#fff' }}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="meanDeviationMm" name="Mean" stroke="#06b6d4" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="maxDeviationMm" name="Max" stroke="#ef4444" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="rmsDeviationMm" name="RMS" stroke="#a855f7" strokeWidth={1.5} dot={false} strokeDasharray="3 3" />
              <ReferenceLine y={25} stroke="#f59e0b" strokeDasharray="5 5" label={{ value: 'default tol 25 mm', fill: '#f59e0b', fontSize: 10 }} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="rounded-xl p-3" style={{ background: '#0f1722', border: '1px solid #1f2937' }} data-testid="trend-chart-within-tol">
        <div className="text-[10px] uppercase tracking-wider opacity-60 mb-2">Within-tolerance % &amp; critical zone count</div>
        {chartData.length > 0 && (
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={chartData}>
              <CartesianGrid stroke="#1f2937" strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fill: '#9ca3af', fontSize: 10 }} />
              <YAxis yAxisId="left" tick={{ fill: '#9ca3af', fontSize: 10 }} unit="%" domain={[0, 100]} />
              <YAxis yAxisId="right" orientation="right" tick={{ fill: '#9ca3af', fontSize: 10 }} />
              <Tooltip contentStyle={{ background: '#0a0e14', border: '1px solid #1f2937', fontSize: 11 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line yAxisId="left" type="monotone" dataKey="withinTolerancePct" name="Within tol %" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
              <Line yAxisId="right" type="monotone" dataKey="criticalZoneCount" name="Critical zones" stroke="#ef4444" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Per-run table */}
      <div className="rounded-xl p-3 overflow-x-auto" style={{ background: '#0f1722', border: '1px solid #1f2937' }}>
        <div className="text-[10px] uppercase tracking-wider opacity-60 mb-2">Run history</div>
        <table className="text-xs w-full" data-testid="trend-table">
          <thead>
            <tr className="opacity-60 text-left">
              <th className="p-1">Date</th>
              <th className="p-1">Code</th>
              <th className="p-1 text-right">Mean</th>
              <th className="p-1 text-right">Max</th>
              <th className="p-1 text-right">Within tol</th>
              <th className="p-1 text-right">Zones</th>
              <th className="p-1 text-right">Critical</th>
              <th className="p-1">Engine</th>
            </tr>
          </thead>
          <tbody>
            {chartData.map((r) => (
              <tr key={r.comparisonCode} className="border-t" style={{ borderColor: '#1f2937' }}>
                <td className="p-1 font-mono opacity-80">{r.date}</td>
                <td className="p-1 font-mono">{r.comparisonCode}</td>
                <td className="p-1 text-right">{r.meanDeviationMm.toFixed(2)}</td>
                <td className="p-1 text-right">{r.maxDeviationMm.toFixed(2)}</td>
                <td className="p-1 text-right">{r.withinTolerancePct.toFixed(1)}%</td>
                <td className="p-1 text-right">{r.zoneCount}</td>
                <td className="p-1 text-right">
                  {r.criticalZoneCount > 0
                    ? <span className="inline-flex items-center gap-0.5 text-red-400"><AlertCircle className="w-3 h-3" /> {r.criticalZoneCount}</span>
                    : <span className="inline-flex items-center gap-0.5 text-emerald-500"><CheckCircle2 className="w-3 h-3" /> 0</span>}
                </td>
                <td className="p-1 text-[10px] uppercase opacity-60">{r.engineMode}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
